import { useCallback } from 'react';
import { Box, IconButton, Stack } from '@mui/material';
import { FormattedMessage, useIntl } from 'react-intl';

import { useShowGraphqlUserVisibleErrorSnackbar } from '@ocx-app/hooks/use-show-graphql-user-visible-error-snackbar.hook';
import { formatPrice } from '@ocx-app/lib/formatters/formatPrice';

import { useConfirmationActionSheet } from '@ocx/feat-modals';
import { LoadingButton, Paper, SizingMultiplier, Typography } from '@ocx/ui';
import { TrashIcon } from '@ocx/ui-icons';
import { logInDev } from '@ocx/utils';

import { allureCashbackMessages } from './allure-cashback.messages';
import {
  AllureCashbackCashout,
  getCashoutIdentifierDisplayValue,
  getCashoutProviderLabelMessage,
} from './allure-cashback.utils';
import { useRedeemAllureCashback } from './use-redeem-allure-cashback.hook';
import { useRemoveAllureCashbackAccount } from './use-remove-allure-cashback-account.hook';

export type AllureCashbackRedeemPageProps = {
  accountUuid: string;
  balance: number;
  cashout: AllureCashbackCashout;
  onRemoved: () => void;
  onRedeemed: () => void;
};

export const AllureCashbackRedeemPage = ({
  accountUuid,
  balance,
  cashout,
  onRemoved,
  onRedeemed,
}: AllureCashbackRedeemPageProps) => {
  const intl = useIntl();
  const isRedeemDisabled = balance <= 0;
  const showGraphqlUserVisibleErrorSnackbar = useShowGraphqlUserVisibleErrorSnackbar();
  const { open: openConfirmationActionSheet } = useConfirmationActionSheet();
  const { redeemCashback, loading: isRedeeming } = useRedeemAllureCashback({
    onCompleted: onRedeemed,
    onError: showGraphqlUserVisibleErrorSnackbar,
  });
  const { removeCashbackAccount, loading: isRemoving } = useRemoveAllureCashbackAccount({
    onCompleted: onRemoved,
    onError: showGraphqlUserVisibleErrorSnackbar,
  });

  const handleRemoveCashoutClick = useCallback(() => {
    openConfirmationActionSheet({
      title: intl.formatMessage(allureCashbackMessages.removeCashoutModalTitle),
      description: intl.formatMessage(allureCashbackMessages.removeCashoutModalDescription),
      confirmButtonColor: 'error',
      confirmButtonText: intl.formatMessage(allureCashbackMessages.removeCashoutModalConfirmButtonText),
      cancelButtonText: intl.formatMessage(allureCashbackMessages.removeCashoutModalCancelButtonText),
      onConfirm: () => {
        removeCashbackAccount(accountUuid).catch(logInDev);
      },
    }).catch(logInDev);
  }, [accountUuid, intl, openConfirmationActionSheet, removeCashbackAccount]);

  return (
    <Stack spacing={SizingMultiplier.md} p={SizingMultiplier.md} sx={{ bgcolor: 'background.default', flex: 1 }}>
      <Paper sx={{ textAlign: 'center', py: SizingMultiplier.lg }}>
        <Typography variant="h2" component="p">
          {formatPrice(balance)}
        </Typography>
        <Typography variant="body2" color="text.secondary" mt={SizingMultiplier.sm}>
          <FormattedMessage {...allureCashbackMessages.redeemBalanceLabel} />
        </Typography>
      </Paper>

      <Paper>
        <Stack spacing={SizingMultiplier.md}>
          <Box
            display="flex"
            alignItems="center"
            gap={SizingMultiplier.sm}
            sx={{ bgcolor: 'action.hover', borderRadius: 2, p: SizingMultiplier.md }}>
            <Box flex={1} minWidth={0}>
              <Typography variant="subtitle1" noWrap>
                {getCashoutIdentifierDisplayValue(cashout)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <FormattedMessage {...getCashoutProviderLabelMessage(cashout)} />
              </Typography>
            </Box>
            <IconButton
              onClick={handleRemoveCashoutClick}
              disabled={isRemoving}
              sx={{
                bgcolor: 'action.selected',
                borderRadius: 1,
                flexShrink: 0,
                '&:hover': { bgcolor: 'action.selected' },
              }}
              aria-label={intl.formatMessage(allureCashbackMessages.removeCashoutButtonAriaLabel)}>
              <TrashIcon color="primary" fontSize="small" />
            </IconButton>
          </Box>
          <LoadingButton
            fullWidth
            loading={isRedeeming}
            disabled={isRedeemDisabled}
            onClick={redeemCashback}
            sx={{ textTransform: 'uppercase' }}>
            <FormattedMessage {...allureCashbackMessages.redeemBalanceButton} />
          </LoadingButton>
        </Stack>
      </Paper>
    </Stack>
  );
};
